import React, { useEffect, useState, useCallback, useRef } from "react";
import { Container, Spinner, Modal, ModalHeader, ModalBody, ModalFooter, Button, Input, Badge, Form, FormGroup, Label, Card, CardBody, Row, Col } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import CommonDataTable from "../../common/DataTable";
import { getBookingColumns } from "../../Components/Common/columns/Bookings";
import { fetchBookings, removeBooking, fetchBookingDetails, fetchAvailableFarms, updateBooking, updateBookingPaymentStatus } from "../../slices/bookings/thunk";
import { fetchSettings } from "../../slices/settings/thunk";
import DeleteDialog from "../../Components/Common/DeleteDailog";
import { showToast } from "../../slices/toast/reducer";
import { getInvoiceHTML } from "./InvoiceTemplate";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.css";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DesktopTimePicker } from "@mui/x-date-pickers/DesktopTimePicker";
import CreateBookingModal from "../../Components/Bookings/CreateBookingModal";

// Custom styles for refined modal appearance
const modalStyles = `
  .custom-modal-refined .modal-header {
    padding: 0.8rem 1.25rem;
    background-color: #fff;
    border-bottom: 1px solid #eee;
  }
  .custom-modal-refined .modal-header .modal-title {
    font-size: 1.05rem;
    font-weight: 600;
  }
  .custom-modal-refined .card {
    border: 1px solid #eef0f2 !important;
  }
  .custom-modal-refined .card-body {
    padding: 1.25rem !important;
  }
  .custom-modal-refined .form-label {
    font-size: 0.825rem !important;
    font-weight: 500;
    margin-bottom: 0.35rem !important;
    color: #495057;
  }
  .custom-modal-refined .form-control, 
  .custom-modal-refined .form-select {
    font-size: 0.875rem !important;
    padding: 0.4rem 0.75rem !important;
    border-color: #dce0e4;
    background-color: #fff !important; /* Remove background */
  }
  .custom-modal-refined .form-control:focus,
  .custom-modal-refined .form-select:focus {
    border-color: #adb5bd !important;
    box-shadow: none !important;
    outline: none !important;
  }
  .custom-modal-refined h6 {
    font-size: 0.9rem !important;
    margin-bottom: 0.75rem !important;
  }
  .custom-modal-refined .row.g-3 {
    --bs-gutter-y: 0.75rem;
    --bs-gutter-x: 1rem;
  }
  
  /* Flatpickr Customization */
  .flatpickr-calendar {
    box-shadow: 0 5px 15px rgba(0,0,0,0.08) !important;
    border: 1px solid #e1e4e8 !important;
    font-size: 13px !important;
  }

  .flatpickr-month {
    color: #000 !important;
    fill: #000 !important;
    height: 34px !important;
  }
  .flatpickr-current-month {
    font-size: 1rem !important;
    padding: 5px 0 0 0 !important;
    color: #00209F !important;
  }
  .flatpickr-monthDropdown-months {
    color: #00209F !important;
    font-weight: 600 !important;
  }
  .flatpickr-current-month .numInputWrapper span.arrowUp:after {
    border-bottom-color: #00209F !important;
  }
  .flatpickr-current-month .numInputWrapper span.arrowDown:after {
    border-top-color: #00209F !important;
  }
  .cur-year {
    color: #00209F !important;
    font-weight: 600 !important;
  }
  .flatpickr-weekday {
    background: #fff !important;
    color: #000 !important;
    font-weight: 600 !important;
  }
  .flatpickr-months .flatpickr-prev-month, 
  .flatpickr-months .flatpickr-next-month {
    color: #000 !important;
    fill: #000 !important;
  }
  .flatpickr-months .flatpickr-prev-month:hover, 
  .flatpickr-months .flatpickr-next-month:hover {
    color: #00209F !important;
    fill: #00209F !important;
  }
  .flatpickr-day.selected, .flatpickr-day.startRange, .flatpickr-day.endRange {
    background: #000 !important;
    border-color: #000 !important;
  }
  .flatpickr-time {
    border-top: 1px solid #eee !important;
  }
  .custom-modal-refined .alert {
    padding: 0.75rem 1rem !important;
    margin-bottom: 1rem !important;
    font-size: 0.85rem !important;
  }

  /* Theme-based Duration Summary Component */
  .duration-summary-box {
    background-color: #fff;
    border: 1px solid #e1e4e8;
    border-radius: 8px;
    padding: 1.25rem;
    margin-bottom: 1.5rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    box-shadow: 0 2px 4px rgba(0,0,0,0.02);
  }
  .duration-title {
    color: #00209F;
    font-weight: 700;
    font-size: 0.95rem;
    margin-bottom: 0.5rem;
  }
  .duration-badge-blue {
    background-color: #2da3db;
    color: white;
    padding: 0.4rem 0.8rem;
    border-radius: 6px;
    font-size: 0.85rem;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    margin-right: 0.5rem;
  }
  .duration-badge-dark {
    background-color: #212529;
    color: white;
    padding: 0.6rem 1.25rem;
    border-radius: 8px;
    font-size: 1rem;
    font-weight: 700;
  }
  .duration-label-small {
    color: #878a99;
    font-size: 0.75rem;
    text-align: right;
    margin-bottom: 0.25rem;
  }
`;

const Bookings = () => {
  const dispatch = useDispatch();
  const { bookings, pagination, loading, bookingDetails, settings } = useSelector((state) => ({
    bookings: state.Bookings.bookings || [],
    pagination: state.Bookings.pagination || {
      page: 1,
      limit: 10,
      totalPages: 0,
      totalBookings: 0
    },
    loading: state.Bookings.loading,
    bookingDetails: state.Bookings.bookingDetails,
    settings: state.Settings?.settings,
  }));

  // Calculate totalRows properly
  const totalRows = pagination.totalBookings || 0;

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'success';
      case 'partial': return 'info';
      case 'incomplete': return 'warning';
      case 'cancel': return 'danger';
      default: return 'secondary';
    }
  };

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState(null);

  // Create modal state
  const [createModalOpen, setCreateModalOpen] = useState(false);

  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const editCheckInTimeRef = useRef(null);
  const editCheckOutTimeRef = useRef(null);
  const [editCheckInPickerOpen, setEditCheckInPickerOpen] = useState(false);
  const [editCheckOutPickerOpen, setEditCheckOutPickerOpen] = useState(false);
  const [editBookingData, setEditBookingData] = useState({
    paidAmount: 0,
    remainingAmount: 0,
    paymentStatus: 'incomplete',
    customerName: "Not specified",
    customerEmail: "",
    customerMobile: ""
  });
  const [isUpdating, setIsUpdating] = useState(false);
  const [editAvailableFarms, setEditAvailableFarms] = useState([]);

  // Payment status modal state
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedBookingForPayment, setSelectedBookingForPayment] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState('');
  const [updatingPayment, setUpdatingPayment] = useState(false);
  const [filterParams, setFilterParams] = useState({
    page: 1,
    limit: 10,
    search: "",
    paymentStatus: "",
    dateFrom: "",
    dateTo: ""
  });
  const [partialPayment, setPartialPayment] = useState({
    paidAmount: 0,
    remainingAmount: 0
  });

  // Check if date is weekend
  const isWeekend = (dateString) => {
    if (!dateString) return false;
    const date = new Date(dateString);
    const day = date.getDay();
    return day === 0 || day === 6;
  };

  const getBookingTypeLabel = (type) => {
    if (!type) return 'Hourly Booking';
    return type === "HOURLY_BOOKING" ? "Hourly Booking" : type;
  };

  const formatTime = (timeString) => {
    if (!timeString) return "7:00 AM";
    try {
      if (timeString.toLowerCase().includes('m')) return timeString;

      // Handle ISO 8601 strings (like from Backend)
      if (timeString.includes('T') || timeString.includes('-')) {
        const date = new Date(timeString);
        if (!isNaN(date.getTime())) {
          return date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          });
        }
      }

      const parts = timeString.split(":");
      if (parts.length < 2) return timeString;

      const hours = parseInt(parts[0], 10);
      const minutes = parts[1].substring(0, 2);
      if (isNaN(hours)) return timeString;

      const ampm = hours >= 12 ? "PM" : "AM";
      const h12 = hours % 12 || 12;
      return `${h12}:${minutes} ${ampm}`;
    } catch {
      return timeString;
    }
  };

  const formatTimeForQuery = (timeString) => {
    if (!timeString) return "";
    try {
      // Convert "07:00" -> "7:00" (matches backend examples)
      const parts = String(timeString).split(":");
      if (parts.length < 2) return String(timeString);
      const hour = parseInt(parts[0], 10);
      const minute = parts[1]?.substring(0, 2);
      if (Number.isNaN(hour) || !minute) return String(timeString);
      return `${hour}:${minute}`;
    } catch {
      return String(timeString);
    }
  };

  const formatBookingDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  useEffect(() => {
    if (!settings) {
      dispatch(fetchSettings());
    }
  }, [dispatch, settings]);

  useEffect(() => {
    // Create a clean copy of filterParams
    const cleanedParams = {};
    Object.keys(filterParams).forEach((key) => {
      const value = filterParams[key];
      if (value !== "" && value !== undefined && value !== null && key !== "currentPage") {
        cleanedParams[key] = value;
      }
    });

    // Ensure page and limit are numbers
    cleanedParams.page = parseInt(cleanedParams.page) || 1;
    cleanedParams.limit = parseInt(cleanedParams.limit) || 10;

    // Only fetch if we have valid parameters
    if (cleanedParams.page > 0 && cleanedParams.limit > 0) {
      dispatch(fetchBookings(cleanedParams));
    }
  }, [dispatch, filterParams]);

  // Replace the current handleFetchData function with this:

  const handleFetchData = useCallback((newParams) => {
    setFilterParams((prevParams) => {
      const updatedParams = {
        ...prevParams,
        ...newParams,
        // Ensure page and limit are numbers
        limit: parseInt(newParams.perPage || newParams.limit || prevParams.limit) || 10,
        page: parseInt(newParams.page || prevParams.page) || 1,
      };

      // Clean up undefined values
      Object.keys(updatedParams).forEach(key => {
        if (updatedParams[key] === undefined || updatedParams[key] === null) {
          delete updatedParams[key];
        }
      });

      return updatedParams;
    });
  }, []);

  // Also, update your useEffect to handle proper param cleaning:
  useEffect(() => {
    // Create a clean copy of filterParams
    const cleanedParams = {};
    Object.keys(filterParams).forEach((key) => {
      const value = filterParams[key];
      if (value !== "" && value !== undefined && value !== null && key !== "currentPage") {
        cleanedParams[key] = value;
      }
    });

    // Ensure page and limit are numbers
    cleanedParams.page = parseInt(cleanedParams.page) || 1;
    cleanedParams.limit = parseInt(cleanedParams.limit) || 10;

    dispatch(fetchBookings(cleanedParams));
  }, [dispatch, filterParams]);

  const handleFilterChange = (key, value) => {
    setFilterParams(prev => ({
      ...prev,
      [key]: value,
      page: 1
    }));
  };

  const clearAllFilters = () => {
    setFilterParams({
      page: 1,
      limit: 10,
      search: "",
      paymentStatus: "",
      dateFrom: "",
      dateTo: ""
    });
  };

  const handleRowClick = (row) => {
    setSelectedBookingId(row.id);
    setDetailsModalOpen(true);
    dispatch(fetchBookingDetails(row.id));
  };

  const handleEditClick = async (row) => {
    try {
      const response = await dispatch(fetchBookingDetails(row.id)).unwrap();
      const booking = response.data || response;

      // Format dates properly for input fields (YYYY-MM-DD)
      const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        return date.toISOString().split('T')[0];
      };

      // Parse time from date string
      const parseTimeFromDate = (timeString) => {
        if (!timeString) return "07:00";
        try {
          // If it's already HH:mm or HH:mm:ss format
          if (typeof timeString === 'string' && !timeString.includes('T') && timeString.includes(':')) {
            return timeString.substring(0, 5);
          }

          const date = new Date(timeString);
          if (isNaN(date.getTime())) return "07:00";
          return date.toTimeString().split(' ')[0].substring(0, 5);
        } catch {
          return "07:00";
        }
      };

      // Determine booking type based on date and existing type
      const bookingDate = formatDateForInput(booking.startDate) || formatDateForInput(booking.bookingDate);
      const isWeekendBooking = isWeekend(bookingDate);
      let finalBookingType = booking.bookingType || "HOURLY_BOOKING";

      // Extract customer information from booking.user
      const customer = booking.user || {};
      const customerMobile = customer.mobile || customer.mobileNo || "";
      const customerEmail = customer.email || "";

      // Determine which contact to show (mobile first, then email)
      const customerContact = customerMobile || customerEmail || "N/A";

      const finalTotalRaw = booking.finalTotal ?? booking.finalPrice ?? 0;
      const finalTotal = Number(finalTotalRaw);
      const finalTotalSafe = Number.isFinite(finalTotal) ? finalTotal : 0;
      const normalizedPaymentStatus = String(booking.paymentStatus || "incomplete").toLowerCase();

      const paidParsed = booking.paidAmount !== null && booking.paidAmount !== undefined && booking.paidAmount !== ""
        ? Number(booking.paidAmount)
        : NaN;
      let paidAmount = Number.isFinite(paidParsed) ? paidParsed : 0;

      const remParsed = booking.remainingAmount !== null && booking.remainingAmount !== undefined && booking.remainingAmount !== ""
        ? Number(booking.remainingAmount)
        : NaN;
      let remainingAmount = Number.isFinite(remParsed)
        ? remParsed
        : Math.max(0, finalTotalSafe - paidAmount);

      if (normalizedPaymentStatus === "paid") {
        paidAmount = finalTotalSafe;
        remainingAmount = 0;
      } else if (normalizedPaymentStatus === "incomplete" || normalizedPaymentStatus === "pending") {
        paidAmount = 0;
        remainingAmount = finalTotalSafe;
      }

      const editData = {
        id: booking.id,
        farmhouseId: booking.farmhouse?.id || null,
        userId: booking.user?.id || null,
        customerName: customer.name || "Not specified",
        customerEmail: customerEmail,
        customerMobile: customerMobile,
        customerContact: customerContact,
        bookingDate: bookingDate || "",
        bookingEndDate: formatDateForInput(booking.endDate) || formatDateForInput(booking.bookingEndDate) || formatDateForInput(booking.startDate) || "",
        bookingTimeFrom: parseTimeFromDate(booking.checkInTime || booking.bookingTimeFrom),
        bookingTimeTo: parseTimeFromDate(booking.checkOutTime || booking.bookingTimeTo),
        numberOfPersons: booking.numberOfPersons || 1,
        bookingType: finalBookingType,
        originalPrice: booking.originalPrice || 0,
        suggestedPrice: booking.originalPrice || 0,
        discountAmount: booking.discountAmount || 0,
        finalPrice: finalTotalSafe,
        paymentStatus: normalizedPaymentStatus,
        farmStatus: booking.farmStatus || "available",
        paidAmount,
        remainingAmount,
        extraPersonCharge: booking.extraPersonInfo?.extraPersonTotalPrice || 0,
        extraPersonPrice: booking.extraPersonInfo?.extraPersonPricePerPerson || (booking.farmhouse?.priceOptions?.find(o => o.category === finalBookingType)?.extraPersonPrice || 0),
        farmMaxPersons: booking.extraPersonInfo?.maxPersons || (booking.farmhouse?.priceOptions?.find(o => o.category === finalBookingType)?.maxPeople || booking.farmhouse?.maxPersons || 10)
      };

      setEditBookingData(editData);

      if (editData.bookingDate) {
        const params = {
          // Backwards compatible params
          date: editData.bookingDate,
          // New time-slot filters
          checkInDate: editData.bookingDate,
          checkOutDate: editData.bookingEndDate || editData.bookingDate,
          checkInFrom: formatTimeForQuery(editData.bookingTimeFrom),
          checkOutTo: formatTimeForQuery(editData.bookingTimeTo),
        };

        if (editData.bookingType) params.bookingType = editData.bookingType;

        dispatch(fetchAvailableFarms(params))
          .unwrap()
          .then((response) => {
            const farms = response?.data?.availableFarms || [];

            // Ensure the current farmhouse is in the list
            if (booking.farmhouse && !farms.some(f => f.id === booking.farmhouse.id)) {
              farms.unshift({
                ...booking.farmhouse,
                name: `${booking.farmhouse.name} (Current)`
              });
            }

            setEditAvailableFarms(farms);
          });
      }

      setEditModalOpen(true);
    } catch (error) {
      console.error("Failed to load booking details:", error);
      dispatch(showToast({ type: "error", msg: "Failed to load booking details" }));
    }
  };



  const handleDeleteClick = (row) => {
    setDeleteId(row.id);
    setDeleteModalOpen(true);
  };

  const handlePaymentStatusClick = (row) => {
    setSelectedBookingForPayment(row);
    setPaymentStatus(row.paymentStatus || 'incomplete');

    // Initialize partial payment based on existing data (use null checks so 0 is not dropped)
    const total = row.finalTotal || row.finalPrice || 0;
    const normalizedRowStatus = String(row.paymentStatus || 'incomplete').toLowerCase();
    const paid =
      row.paidAmount !== null && row.paidAmount !== undefined && row.paidAmount !== ""
        ? Number(row.paidAmount)
        : 0;
    const remaining =
      row.remainingAmount !== null && row.remainingAmount !== undefined && row.remainingAmount !== ""
        ? Number(row.remainingAmount)
        : Math.max(0, total - paid);

    const normalizedPaid = normalizedRowStatus === 'paid'
      ? total
      : (normalizedRowStatus === 'incomplete' || normalizedRowStatus === 'pending' ? 0 : paid);
    const normalizedRemaining = normalizedRowStatus === 'paid'
      ? 0
      : (normalizedRowStatus === 'incomplete' || normalizedRowStatus === 'pending'
        ? total
        : Math.max(0, remaining));

    setPartialPayment({
      paidAmount: normalizedPaid,
      remainingAmount: normalizedRemaining
    });
    setPaymentModalOpen(true);
  };

  useEffect(() => {
    if (paymentStatus === 'partial' && selectedBookingForPayment) {
      const total = selectedBookingForPayment.finalTotal || selectedBookingForPayment.finalPrice || 0;
      setPartialPayment({
        paidAmount: partialPayment.paidAmount || 0,
        remainingAmount: partialPayment.remainingAmount || total
      });
    }
  }, [paymentStatus, selectedBookingForPayment]);

  // Calculate end date for 24hr bookings
  const calculateEndDateForCreate = (startDate, type) => {
    if ((type === "REGULAR_24HR" || type === "WEEKEND_24HR") && startDate) {
      const date = new Date(startDate);
      date.setDate(date.getDate() + 1);
      return date.toISOString().split('T')[0];
    }
    return startDate;
  };

  const calculateDurationAndRent = useCallback((startDate, endDate, startTime, endTime, farmhouse) => {
    if (!startDate || !endDate || !farmhouse) return { hours: 0, rent: 0 };
    try {
      const start = new Date(`${startDate}T${startTime || '07:00'}`);
      const end = new Date(`${endDate}T${endTime || '18:00'}`);
      const durationMs = end - start;
      const actualHours = Math.round(durationMs / (1000 * 60 * 60));

      if (actualHours <= 0) return { hours: 0, rent: 0 };

      const isWknd = isWeekend(startDate);
      const typePrefix = isWknd ? "WEEKEND" : "REGULAR";

      const price12h = farmhouse.priceOptions?.find(o => o.category === `${typePrefix}_12HR`)?.price || farmhouse.price || 0;
      const price24h = farmhouse.priceOptions?.find(o => o.category === `${typePrefix}_24HR`)?.price || (price12h * 1.8);

      let totalRent = 0;
      let billingHours = 0;

      if (actualHours <= 12) {
        billingHours = 12;
        totalRent = price12h;
      } else if (actualHours <= 24) {
        billingHours = 24;
        totalRent = price24h;
      } else {
        // Use 12-hour increments for stays over 24 hours
        billingHours = Math.ceil(actualHours / 12) * 12;

        const fullDays = Math.floor(actualHours / 24);
        const remainingHours = actualHours % 24;

        totalRent = fullDays * price24h;
        if (remainingHours > 0) {
          if (remainingHours <= 12) {
            totalRent += price12h;
          } else {
            totalRent += price24h;
          }
        }
      }
      return { hours: billingHours, rent: totalRent };
    } catch (e) {
      return { hours: 0, rent: 0 };
    }
  }, []);

  // Duration calculation for display
  const duration = (() => {
    const { hours } = calculateDurationAndRent(
      editBookingData.bookingDate,
      editBookingData.bookingEndDate,
      editBookingData.bookingTimeFrom,
      editBookingData.bookingTimeTo,
      editAvailableFarms.find(f => f.id === editBookingData.farmhouseId) || { price: editBookingData.originalPrice }
    );
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    return { hours, days, remainingHours };
  })();

  const openCreateModal = () => {
    setCreateModalOpen(true);
  };


  const handleUpdateBooking = async () => {
    if (!editBookingData.farmhouseId || !editBookingData.bookingDate) {
      dispatch(showToast({ type: "warning", msg: "Please select farmhouse and booking date" }));
      return;
    }

    setIsUpdating(true);
    try {
      const finalPriceNum = Number(editBookingData.finalPrice);
      const finalPriceSafe = Number.isFinite(finalPriceNum) ? finalPriceNum : 0;
      const paidRaw = editBookingData.paidAmount;
      const paidNum =
        paidRaw === "" || paidRaw === null || paidRaw === undefined
          ? 0
          : (Number(paidRaw) || 0);
      const remRaw = editBookingData.remainingAmount;
      const remainingNum =
        remRaw === "" || remRaw === null || remRaw === undefined
          ? Math.max(0, finalPriceSafe - paidNum)
          : (Number(remRaw) || 0);

      const bookingData = {
        farmhouseId: editBookingData.farmhouseId,
        userId: editBookingData.userId,
        bookingDate: editBookingData.bookingDate,
        bookingEndDate: editBookingData.bookingEndDate || editBookingData.bookingDate,
        bookingTimeFrom: editBookingData.bookingTimeFrom,
        bookingTimeTo: editBookingData.bookingTimeTo,
        numberOfPersons: editBookingData.numberOfPersons === "" ? 1 : (Number(editBookingData.numberOfPersons) || 1),
        bookingType: editBookingData.bookingType,
        originalPrice: editBookingData.originalPrice,
        discountAmount: editBookingData.discountAmount,
        finalPrice: editBookingData.finalPrice,
        paymentStatus: editBookingData.paymentStatus,
        farmStatus: editBookingData.farmStatus,
        paidAmount: paidNum,
        remainingAmount: remainingNum,
        bookingHours: (() => {
          const start = new Date(`${editBookingData.bookingDate}T${editBookingData.bookingTimeFrom || '07:00'}`);
          const end = new Date(`${editBookingData.bookingEndDate}T${editBookingData.bookingTimeTo || '18:00'}`);
          const actualHours = Math.round((end - start) / (1000 * 60 * 60));
          return (Math.ceil(Math.max(0, actualHours) / 12) * 12).toString();
        })(),
        bookingData: {
          extraPersonCharge: editBookingData.extraPersonCharge || 0,
          extraPersonPrice: editBookingData.extraPersonPrice || 0,
        },
      };

      await dispatch(updateBooking({ id: editBookingData.id, data: bookingData })).unwrap();

      setEditModalOpen(false);
      const cleanedParams = {};
      Object.keys(filterParams).forEach((key) => {
        const value = filterParams[key];
        if (value !== "" && value !== undefined && value !== null && key !== "currentPage") {
          cleanedParams[key] = value;
        }
      });
      dispatch(fetchBookings(cleanedParams));
      dispatch(showToast({ type: "success", msg: "Booking updated successfully" }));
    } catch (error) {
      console.error("Failed to update booking:", error);
      dispatch(showToast({ type: "error", msg: "Failed to update booking" }));
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await dispatch(removeBooking(deleteId)).unwrap();
      setDeleteModalOpen(false);
      setDeleteId(null);
      const cleanedParams = {};
      Object.keys(filterParams).forEach((key) => {
        const value = filterParams[key];
        if (value !== "" && value !== undefined && value !== null && key !== "currentPage") {
          cleanedParams[key] = value;
        }
      });
      dispatch(fetchBookings(cleanedParams));
      dispatch(showToast({ type: "success", msg: "Booking deleted successfully" }));
    } catch (error) {
      console.error("Error deleting booking:", error);
      dispatch(showToast({ type: "error", msg: "Failed to delete booking" }));
    } finally {
      setDeleting(false);
    }
  };

  const handleUpdatePaymentStatus = async () => {
    if (!selectedBookingForPayment || !paymentStatus) return;

    if (paymentStatus === 'partial' && (!partialPayment.paidAmount || partialPayment.paidAmount <= 0)) {
      dispatch(showToast({ type: "warning", msg: "Please enter paid amount for outstanding payment" }));
      return;
    }

    setUpdatingPayment(true);
    try {
      let paymentData = {
        paymentStatus: paymentStatus
      };

      const total = selectedBookingForPayment.finalTotal || selectedBookingForPayment.finalPrice || 0;

      if (paymentStatus === 'partial') {
        const paidNum = Number(partialPayment.paidAmount) || 0;
        paymentData = {
          ...paymentData,
          paidAmount: paidNum,
          remainingAmount: Number(partialPayment.remainingAmount) || (total - paidNum),
          notes: 'Payment updated via admin panel'
        };
      } else if (paymentStatus === 'paid') {
        paymentData = {
          ...paymentData,
          paidAmount: total,
          remainingAmount: 0
        };
      } else if (paymentStatus === 'incomplete') {
        paymentData = {
          ...paymentData,
          paidAmount: 0,
          remainingAmount: total
        };
      } else if (paymentStatus === 'cancel') {
        paymentData = {
          ...paymentData,
          paidAmount: 0,
          remainingAmount: total
        };
      }

      await dispatch(updateBookingPaymentStatus({
        id: selectedBookingForPayment.id,
        data: paymentData
      })).unwrap();

      dispatch(showToast({
        type: "success",
        msg: `Payment status updated to ${paymentStatus}${paymentStatus === 'partial' ? ` (₹${Number(partialPayment.paidAmount) || 0} paid)` : ''}`
      }));

      setPaymentModalOpen(false);
      setPartialPayment({ paidAmount: 0, remainingAmount: 0 });

      const cleanedParams = {};
      Object.keys(filterParams).forEach((key) => {
        const value = filterParams[key];
        if (value !== "" && value !== undefined && value !== null && key !== "currentPage") {
          cleanedParams[key] = value;
        }
      });
      dispatch(fetchBookings(cleanedParams));
    } catch (error) {
      console.error("Failed to update payment status:", error);
      dispatch(showToast({ type: "error", msg: error.message || "Failed to update payment status" }));
    } finally {
      setUpdatingPayment(false);
    }
  };

  const waitForInvoiceAssets = async (container) => {
    if (document.fonts && document.fonts.ready) {
      try {
        await document.fonts.ready;
      } catch (e) {
        // Continue PDF generation even if font readiness fails.
      }
    }

    const images = Array.from(container.querySelectorAll("img"));
    await Promise.all(images.map((img) => {
      if (img.complete) return Promise.resolve();

      return new Promise((resolve) => {
        const cleanup = () => {
          img.removeEventListener("load", onDone);
          img.removeEventListener("error", onDone);
        };
        const onDone = () => {
          cleanup();
          resolve();
        };

        // Avoid hanging invoice generation forever.
        const timeoutId = setTimeout(() => {
          cleanup();
          resolve();
        }, 5000);

        img.addEventListener("load", () => {
          clearTimeout(timeoutId);
          onDone();
        });
        img.addEventListener("error", () => {
          clearTimeout(timeoutId);
          onDone();
        });
      });
    }));
  };

  const handleInvoiceDownload = async (booking) => {
    let container;
    try {
      if (!booking) {
        dispatch(showToast({ type: "error", msg: "Booking details unavailable for invoice" }));
        return;
      }

      const invoiceHTML = getInvoiceHTML(booking, settings);

      // Create a temporary container element
      container = document.createElement("div");
      container.style.position = "fixed";
      container.style.left = "-9999px";
      container.style.top = "0";
      container.style.zIndex = "-1";
      container.style.background = "#fff";
      container.innerHTML = invoiceHTML;
      document.body.appendChild(container);
      await waitForInvoiceAssets(container);

      const invoiceElement = container.querySelector(".invoice-wrapper");
      const rulesElement = container.querySelector(".rules-page");

      // Create PDF
      const pdf = new jsPDF("p", "pt", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfPageHeight = pdf.internal.pageSize.getHeight();

      if (invoiceElement && rulesElement) {
        // Page 1: Render Invoice
        const canvas1 = await html2canvas(invoiceElement, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          logging: false,
          backgroundColor: "#ffffff"
        });
        const imgData1 = canvas1.toDataURL("image/png");
        const imageHeight1 = (canvas1.height * pdfWidth) / canvas1.width;
        const scale1 = Math.min(1, pdfPageHeight / imageHeight1);
        const drawWidth1 = pdfWidth * scale1;
        const drawHeight1 = imageHeight1 * scale1;
        const x1 = (pdfWidth - drawWidth1) / 2;
        pdf.addImage(imgData1, "PNG", x1, 0, drawWidth1, drawHeight1, undefined, "FAST");

        // Page 2: Render Rules
        pdf.addPage();
        const canvas2 = await html2canvas(rulesElement, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          logging: false,
          backgroundColor: "#ffffff"
        });
        const imgData2 = canvas2.toDataURL("image/png");
        const imageHeight2 = (canvas2.height * pdfWidth) / canvas2.width;
        const scale2 = Math.min(1, pdfPageHeight / imageHeight2);
        const drawWidth2 = pdfWidth * scale2;
        const drawHeight2 = imageHeight2 * scale2;
        const x2 = (pdfWidth - drawWidth2) / 2;
        pdf.addImage(imgData2, "PNG", x2, 0, drawWidth2, drawHeight2, undefined, "FAST");
      } else {
        // Fallback: Render entire container as single-page PDF
        const canvas = await html2canvas(container, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          logging: false,
          backgroundColor: "#ffffff"
        });
        const imgData = canvas.toDataURL("image/png");
        const imageHeight = (canvas.height * pdfWidth) / canvas.width;
        const scale = Math.min(1, pdfPageHeight / imageHeight);
        const drawWidth = pdfWidth * scale;
        const drawHeight = imageHeight * scale;
        const x = (pdfWidth - drawWidth) / 2;
        pdf.addImage(imgData, "PNG", x, 0, drawWidth, drawHeight, undefined, "FAST");
      }

      // Generate filename using customer name
      const customerName = booking.user?.name || booking.userName || "Customer";
      // Sanitize filename: remove special characters, replace spaces with hyphens, limit length
      const sanitizedName = customerName
        .replace(/[^a-zA-Z0-9\s-]/g, "") // Remove special characters
        .replace(/\s+/g, "-") // Replace spaces with hyphens
        .toLowerCase()
        .substring(0, 50); // Limit to 50 characters
      const invoiceDate = booking.updatedAt || booking.createdAt || new Date();
      const parsedInvoiceDate = new Date(invoiceDate);
      const dateStr = Number.isNaN(parsedInvoiceDate.getTime())
        ? new Date().toISOString().split("T")[0]
        : parsedInvoiceDate.toISOString().split("T")[0]; // Format: YYYY-MM-DD
      const fileName = `Invoice-${sanitizedName}-${dateStr}.pdf`;

      pdf.save(fileName);
    } catch (error) {
      console.error("Error downloading invoice:", error);
      dispatch(showToast({ type: "error", msg: "Failed to download invoice" }));
    } finally {
      if (container && container.parentNode) {
        container.parentNode.removeChild(container);
      }
    }
  };

  const handleInvoiceView = (booking) => {
    if (booking && booking.id) {
      window.open(`/invoice/${booking.id}`, '_blank');
    }
  };

  const toDateString = (d) =>
    d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');

  const handleEditCheckInDateChange = (dates) => {
    if (dates.length > 0) {
      const startDateString = toDateString(dates[0]);
      const endDateString = editBookingData.bookingEndDate && editBookingData.bookingEndDate < startDateString
        ? startDateString
        : (editBookingData.bookingEndDate || startDateString);

      const selectedFarm = editAvailableFarms.find(f => f.id === editBookingData.farmhouseId);
      let newOriginalPrice = editBookingData.originalPrice;
      let extraCharge = editBookingData.extraPersonCharge || 0;
      let extraPrice = editBookingData.extraPersonPrice || 0;
      let maxPeople = editBookingData.farmMaxPersons || 10;
      let newBookingType = editBookingData.bookingType || "HOURLY_BOOKING";

      if (selectedFarm) {
        const { rent, hours } = calculateDurationAndRent(
          startDateString,
          endDateString,
          editBookingData.bookingTimeFrom,
          editBookingData.bookingTimeTo,
          selectedFarm
        );
        newOriginalPrice = rent || selectedFarm.price || 0;

        const typePrefix = isWeekend(startDateString) ? "WEEKEND" : "REGULAR";
        const derivedType = (hours > 12 ? "24HR" : "12HR");
        newBookingType = `${typePrefix}_${derivedType}`;
        const priceOption = selectedFarm.priceOptions?.find((o) => o.category === newBookingType);
        maxPeople = priceOption?.maxPeople || selectedFarm.maxPersons || 10;
        extraPrice = priceOption?.extraPersonPrice || 0;

        const personsNum = Number(editBookingData.numberOfPersons) || 1;
        if (personsNum > maxPeople) {
          extraCharge = (personsNum - maxPeople) * extraPrice;
        } else {
          extraCharge = 0;
        }
      }

      setEditBookingData(prev => {
        const finalPrice = (prev.originalPrice || 0) + extraCharge - (prev.discountAmount || 0);
        return {
          ...prev,
          bookingDate: startDateString,
          bookingEndDate: endDateString,
          bookingType: newBookingType,
          suggestedPrice: newOriginalPrice,
          extraPersonCharge: extraCharge,
          extraPersonPrice: extraPrice,
          farmMaxPersons: maxPeople,
          finalPrice: finalPrice,
          remainingAmount: Math.max(0, finalPrice - (prev.paidAmount || 0))
        };
      });

      const params = {
        // Backwards compatible params
        date: startDateString,
        bookingType: newBookingType,
        // New time-slot filters
        checkInDate: startDateString,
        checkOutDate: endDateString,
        checkInFrom: formatTimeForQuery(editBookingData.bookingTimeFrom),
        checkOutTo: formatTimeForQuery(editBookingData.bookingTimeTo),
      };
      dispatch(fetchAvailableFarms(params))
        .unwrap()
        .then((response) => {
          const farms = response?.data?.availableFarms || [];
          setEditAvailableFarms(farms);
        });
    }
  };

  const handleEditCheckOutDateChange = (dates) => {
    if (dates.length > 0) {
      const endDateString = toDateString(dates[0]);
      const startDateString = editBookingData.bookingDate || endDateString;

      const selectedFarm = editAvailableFarms.find(f => f.id === editBookingData.farmhouseId);
      let newOriginalPrice = editBookingData.originalPrice;
      let extraCharge = editBookingData.extraPersonCharge || 0;
      let extraPrice = editBookingData.extraPersonPrice || 0;
      let maxPeople = editBookingData.farmMaxPersons || 10;
      let newBookingType = editBookingData.bookingType || "HOURLY_BOOKING";

      if (selectedFarm) {
        const { rent, hours } = calculateDurationAndRent(
          startDateString,
          endDateString,
          editBookingData.bookingTimeFrom,
          editBookingData.bookingTimeTo,
          selectedFarm
        );
        newOriginalPrice = rent || selectedFarm.price || 0;

        const typePrefix = isWeekend(startDateString) ? "WEEKEND" : "REGULAR";
        const derivedType = (hours > 12 ? "24HR" : "12HR");
        newBookingType = `${typePrefix}_${derivedType}`;
        const priceOption = selectedFarm.priceOptions?.find((o) => o.category === newBookingType);
        maxPeople = priceOption?.maxPeople || selectedFarm.maxPersons || 10;
        extraPrice = priceOption?.extraPersonPrice || 0;

        const personsNum = Number(editBookingData.numberOfPersons) || 1;
        if (personsNum > maxPeople) {
          extraCharge = (personsNum - maxPeople) * extraPrice;
        } else {
          extraCharge = 0;
        }
      }

      setEditBookingData(prev => {
        const finalPrice = (prev.originalPrice || 0) + extraCharge - (prev.discountAmount || 0);
        return {
          ...prev,
          bookingEndDate: endDateString,
          bookingType: newBookingType,
          suggestedPrice: newOriginalPrice,
          extraPersonCharge: extraCharge,
          extraPersonPrice: extraPrice,
          farmMaxPersons: maxPeople,
          finalPrice: finalPrice,
          remainingAmount: Math.max(0, finalPrice - (prev.paidAmount || 0))
        };
      });
    }
  };

  const handleEditDateChange = (newDate) => {
    let newBookingType = "HOURLY_BOOKING";

    const endDate = (newBookingType === "REGULAR_24HR" || newBookingType === "WEEKEND_24HR")
      ? calculateEndDateForCreate(newDate, newBookingType)
      : newDate;

    setEditBookingData(prev => ({
      ...prev,
      bookingDate: newDate,
      bookingType: newBookingType,
      bookingEndDate: endDate
    }));

    if (newDate) {
      const params = {
        // Backwards compatible params
        date: newDate,
        bookingType: newBookingType,
        // New time-slot filters
        checkInDate: newDate,
        checkOutDate: endDate,
        checkInFrom: formatTimeForQuery(editBookingData.bookingTimeFrom),
        checkOutTo: formatTimeForQuery(editBookingData.bookingTimeTo),
      };
      dispatch(fetchAvailableFarms(params))
        .unwrap()
        .then((response) => {
          const farms = response?.data?.availableFarms || [];
          setEditAvailableFarms(farms);
        });
    }
  };

  const handleEditBookingTypeChange = (newType) => {
    const endDate = (newType === "REGULAR_24HR" || newType === "WEEKEND_24HR")
      ? calculateEndDateForCreate(editBookingData.bookingDate, newType)
      : editBookingData.bookingDate;

    const nextBookingTimeFrom = (newType && newType.includes("24HR")) ? "19:00" : "07:00";
    const nextBookingTimeTo = "18:00";

    setEditBookingData(prev => ({
      ...prev,
      bookingType: newType,
      bookingEndDate: endDate,
      bookingTimeFrom: nextBookingTimeFrom,
      bookingTimeTo: nextBookingTimeTo
    }));

    if (editBookingData.bookingDate) {
      const params = {
        // Backwards compatible params
        date: editBookingData.bookingDate,
        bookingType: newType,
        // New time-slot filters
        checkInDate: editBookingData.bookingDate,
        checkOutDate: endDate,
        checkInFrom: formatTimeForQuery(nextBookingTimeFrom),
        checkOutTo: formatTimeForQuery(nextBookingTimeTo),
      };
      dispatch(fetchAvailableFarms(params))
        .unwrap()
        .then((response) => {
          const farms = response?.data?.availableFarms || [];
          setEditAvailableFarms(farms);
        });
    }
  };

  // Custom columns with payment details
  const getCustomColumns = () => {
    const baseColumns = getBookingColumns({
      onDeleteClick: handleDeleteClick,
      onEditClick: handleEditClick,
      onRowClick: handleRowClick,
      onInvoiceClick: handleInvoiceDownload,
      onInvoiceViewClick: handleInvoiceView,
      onPaymentStatusClick: handlePaymentStatusClick,
    });

    // Add payment details to payment status column
    return baseColumns.map(column => {
      if (column.accessor === 'paymentStatus') {
        return {
          ...column,
          Cell: ({ row }) => {
            const paymentStatus = row.original.paymentStatus;
            const total = row.original.finalTotal || row.original.finalPrice || 0;
            const paid = row.original.paidAmount || 0;
            const remaining = row.original.remainingAmount || (total - paid);

            return (
              <div>
                <Badge color={getPaymentStatusColor(paymentStatus)} className="mb-1">
                  {paymentStatus === 'partial' ? 'Outstanding' : paymentStatus}
                </Badge>
                {paymentStatus === 'partial' && (
                  <div className="small text-muted">
                    <div>Paid: ₹{paid}</div>
                    <div>Remaining: ₹{remaining}</div>
                  </div>
                )}
              </div>
            );
          }
        };
      }
      return column;
    });
  };

  const columns = getCustomColumns();

  const handleSearch = (searchValue) => {
    setFilterParams(prev => ({
      ...prev,
      search: searchValue,
      page: 1
    }));
  };



  const CustomFilters = () => {
    return (
      <div className="advanced-filters-panel bookings-filters-panel mb-2" style={{ marginTop: "-1rem" }}>
        <Row className="g-sm-3 bookings-filters-row">
          <Col xs={12} sm={6} md={4}>
            <FormGroup>
              <Label for="paymentStatusFilter" className="fw-medium small">Payment Status</Label>
              <Input
                type="select"
                id="paymentStatusFilter"
                value={filterParams.paymentStatus || ''}
                onChange={(e) => handleFilterChange('paymentStatus', e.target.value)}
                className="form-select-sm"
              >
                <option value="">All Status</option>
                <option value="paid">Paid</option>
                <option value="partial">Outstanding</option>
                <option value="incomplete">Incomplete</option>
                <option value="cancel">Cancelled</option>
              </Input>
            </FormGroup>
          </Col>

          <Col xs={12} sm={6} md={4}>
            <FormGroup>
              <Label for="dateFromFilter" className="fw-medium small">Date From</Label>
              <Input
                type="date"
                id="dateFromFilter"
                value={filterParams.dateFrom || ''}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                className="form-control-sm"
              />
            </FormGroup>
          </Col>

          <Col xs={12} sm={6} md={4}>
            <FormGroup>
              <Label for="dateToFilter" className="fw-medium small">Date To</Label>
              <Input
                type="date"
                id="dateToFilter"
                value={filterParams.dateTo || ''}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                className="form-control-sm"
              />
            </FormGroup>
          </Col>
        </Row>

        {(filterParams.paymentStatus || filterParams.dateFrom || filterParams.dateTo) && (
          <div className="bookings-active-filters mt-3 pt-3 border-top">
            <small className="text-muted me-2">Active Filters:</small>
            {filterParams.paymentStatus && (
              <Badge color="info" className="me-2">
                Payment: {filterParams.paymentStatus}
                <i
                  className="ri-close-line ms-1 cursor-pointer"
                  onClick={() => handleFilterChange('paymentStatus', '')}
                  style={{ cursor: 'pointer' }}
                ></i>
              </Badge>
            )}
            {filterParams.dateFrom && (
              <Badge color="warning" className="me-2">
                From: {filterParams.dateFrom}
                <i
                  className="ri-close-line ms-1 cursor-pointer"
                  onClick={() => handleFilterChange('dateFrom', '')}
                  style={{ cursor: 'pointer' }}
                ></i>
              </Badge>
            )}
            {filterParams.dateTo && (
              <Badge color="warning" className="me-2">
                To: {filterParams.dateTo}
                <i
                  className="ri-close-line ms-1 cursor-pointer"
                  onClick={() => handleFilterChange('dateTo', '')}
                  style={{ cursor: 'pointer' }}
                ></i>
              </Badge>
            )}
            <Button
              color="link"
              size="sm"
              onClick={clearAllFilters}
              className="text-danger ms-2"
            >
              <i className="ri-close-circle-line me-1"></i>
              Clear All
            </Button>
          </div>
        )}

        <div className="bookings-mobile-create d-md-none mt-2">
          <Button color="primary" className="w-100" onClick={openCreateModal}>
            <i className="ri-add-line me-1"></i>
            Create Booking
          </Button>
        </div>
      </div>
    );
  };

  return (
    <React.Fragment>
      <style>{modalStyles}</style>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Bookings" pageTitle="Bookings" />
        </Container>
      </div>

      <div className="table-container bookings-table-container">
        {loading && bookings.length === 0 ? (
          <div className="text-center p-5">
            <Spinner color="primary" />
            <p className="mt-2">Loading bookings...</p>
          </div>
        ) : (
          <CommonDataTable
            columns={columns}
            data={bookings}
            loading={loading}
            totalRows={totalRows}
            fetchData={handleFetchData}
            filterParams={filterParams}
            searchEnable={true}
            onSearch={handleSearch}
            filterComponent={<CustomFilters />}
            checkboxEnabled={false}
            showExportButton={false}
            exportFileName="Bookings"
            exportColumns={columns}
            showAddButton={true}
            buttonName={"Create Booking"}
            addOnClickMethod={openCreateModal}
            paginationPerPage={pagination.limit}
            paginationDefaultPage={pagination.page}
            paginationTotalRows={totalRows}
          />
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteDialog
        isOpen={deleteModalOpen}
        title="Delete Booking"
        message="Are you sure you want to delete this booking?"
        confirmLabel="Delete"
        cancelLabel="Cancel"
        confirmColor="danger"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteModalOpen(false)}
      />

      <CreateBookingModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        skipUserStep={false}
        preselectedUser={null}
        onBookingCreated={() => {
          const cleanedParams = {};
          Object.keys(filterParams).forEach((key) => {
            const value = filterParams[key];
            if (value !== "" && value !== undefined && value !== null && key !== "currentPage") {
              cleanedParams[key] = value;
            }
          });
          dispatch(fetchBookings(cleanedParams));
        }}
        modalClassName="custom-modal-refined"
      />

      {/* Payment Status Update Modal */}
      <Modal isOpen={paymentModalOpen} toggle={() => setPaymentModalOpen(false)} centered className="full-height-model custom-modal-refined">
        <ModalHeader toggle={() => setPaymentModalOpen(false)} style={{ borderBottom: '1px solid #dee2e6' }}>
          <i className="ri-bank-card-line me-2"></i>Update Payment Status
        </ModalHeader>
        <ModalBody style={{ flex: 1, overflowY: 'auto', paddingTop: '1rem', paddingBottom: '1rem' }}>
          {selectedBookingForPayment && (
            <div>
              <div className="alert alert-info mb-4">
                <i className="ri-information-line me-2"></i>
                Update payment status manually. This will not process any actual payment.
              </div>

              <div className="mb-4">
                <h6 className="mb-3">Booking Information:</h6>
                <div className="row">
                  <div className="col-6">
                    <small className="text-muted d-block">Booking ID</small>
                    <span className="fw-medium">#{selectedBookingForPayment.id}</span>
                  </div>
                  <div className="col-6">
                    <small className="text-muted d-block">Customer</small>
                    <span className="fw-medium">{selectedBookingForPayment.user?.name || 'N/A'}</span>
                  </div>
                  <div className="col-6 mt-2">
                    <small className="text-muted d-block">Farmhouse</small>
                    <span className="fw-medium">{selectedBookingForPayment.farmhouse?.name || 'N/A'}</span>
                  </div>
                  <div className="col-6 mt-2">
                    <small className="text-muted d-block">Total Amount</small>
                    <span className="fw-bold text-success">₹{selectedBookingForPayment.finalTotal || selectedBookingForPayment.finalPrice || 0}</span>
                  </div>
                </div>
              </div>

              <FormGroup className="mb-4">
                <Label for="paymentStatusSelect" className="fw-medium">Payment Status</Label>
                <Input
                  type="select"
                  id="paymentStatusSelect"
                  value={paymentStatus}
                  onChange={(e) => {
                    const newStatus = e.target.value;
                    const total = selectedBookingForPayment.finalTotal || selectedBookingForPayment.finalPrice || 0;

                    setPaymentStatus(newStatus);

                    if (newStatus === 'paid') {
                      setPartialPayment({
                        paidAmount: total,
                        remainingAmount: 0
                      });
                    } else if (newStatus === 'incomplete') {
                      setPartialPayment({
                        paidAmount: 0,
                        remainingAmount: total
                      });
                    } else if (newStatus === 'partial') {
                      const paid = partialPayment.paidAmount || Math.floor(total / 2);
                      setPartialPayment({
                        paidAmount: paid,
                        remainingAmount: total - paid
                      });
                    } else if (newStatus === 'cancel') {
                      setPartialPayment({
                        paidAmount: 0,
                        remainingAmount: total
                      });
                    }
                  }}
                  className="form-select"
                >
                  <option value="incomplete">Not Paid</option>
                  <option value="partial">Outstanding</option>
                  <option value="paid">Paid</option>
                  <option value="cancel">Cancel</option>
                </Input>
              </FormGroup>

              {paymentStatus === 'partial' && (
                <div className="mb-4 p-3 border rounded" style={{ backgroundColor: '#f5f5f5' }}>
                  <h6 className="mb-3">
                    <i className="ri-money-dollar-circle-line me-2"></i>
                    Payment Details
                  </h6>

                  <FormGroup className="mb-3">
                    <Label for="paidAmountModal" className="fw-medium">
                      Paid Amount (₹)
                      <small className="text-muted ms-1">(Editable)</small>
                    </Label>
                    <Input
                      type="number"
                      id="paidAmountModal"
                      value={partialPayment.paidAmount === "" ? "" : (partialPayment.paidAmount === 0 || partialPayment.paidAmount == null ? "0" : partialPayment.paidAmount)}
                      onChange={(e) => {
                        const raw = e.target.value;
                        const paid = raw === "" ? "" : (parseFloat(raw) || 0);
                        const total = selectedBookingForPayment.finalTotal || selectedBookingForPayment.finalPrice || 0;
                        const paidNum = raw === "" ? 0 : (parseFloat(raw) || 0);
                        const remaining = Math.max(0, total - paidNum);

                        setPartialPayment({
                          paidAmount: paid,
                          remainingAmount: remaining
                        });
                      }}
                      onFocus={(e) => {
                        if (e.target.value === "0" || e.target.value === 0 || parseFloat(e.target.value) === 0) {
                          e.target.value = "";
                          setPartialPayment(prev => ({
                            ...prev,
                            paidAmount: "",
                          }));
                        }
                      }}
                      onBlur={(e) => {
                        if (e.target.value === "" || e.target.value === null) {
                          const total = selectedBookingForPayment.finalTotal || selectedBookingForPayment.finalPrice || 0;
                          const remaining = Math.max(0, total - 0);
                          setPartialPayment(prev => ({
                            ...prev,
                            paidAmount: 0,
                            remainingAmount: remaining
                          }));
                        }
                      }}
                      placeholder="Enter paid amount"
                      min="0"
                      step="0.01"
                      max={selectedBookingForPayment.finalTotal || selectedBookingForPayment.finalPrice || 0}
                      className="form-control"
                    />
                    <small className="text-muted mt-1 d-block">
                      Paid: ₹{partialPayment.paidAmount || 0} |
                      Remaining: ₹{partialPayment.remainingAmount || 0}
                    </small>
                  </FormGroup>

                  <FormGroup className="mb-3">
                    <Label for="remainingAmountModal" className="fw-medium">
                      Remaining Amount (₹)
                      <small className="text-muted ms-1">(Auto-calculated)</small>
                    </Label>
                    <Input
                      type="number"
                      id="remainingAmountModal"
                      value={partialPayment.remainingAmount === "" ? "" : (partialPayment.remainingAmount == null ? (selectedBookingForPayment.finalTotal || selectedBookingForPayment.finalPrice || 0) : partialPayment.remainingAmount)}
                      onChange={(e) => {
                        const raw = e.target.value;
                        const remaining = raw === "" ? "" : (parseFloat(raw) || 0);
                        const total = selectedBookingForPayment.finalTotal || selectedBookingForPayment.finalPrice || 0;
                        const remainingNum = raw === "" ? 0 : (parseFloat(raw) || 0);
                        const paid = Math.max(0, total - remainingNum);

                        setPartialPayment({
                          paidAmount: paid,
                          remainingAmount: remaining
                        });
                      }}
                      onFocus={(e) => {
                        const defaultValue = selectedBookingForPayment.finalTotal || selectedBookingForPayment.finalPrice || 0;
                        if (e.target.value === "0" || e.target.value === 0 || (parseFloat(e.target.value) === defaultValue && defaultValue > 0)) {
                          e.target.value = "";
                          setPartialPayment(prev => ({
                            ...prev,
                            remainingAmount: "",
                          }));
                        }
                      }}
                      onBlur={(e) => {
                        if (e.target.value === "" || e.target.value === null) {
                          const total = selectedBookingForPayment.finalTotal || selectedBookingForPayment.finalPrice || 0;
                          const paid = partialPayment.paidAmount || 0;
                          const remaining = Math.max(0, total - paid);
                          setPartialPayment(prev => ({
                            ...prev,
                            remainingAmount: remaining
                          }));
                        }
                      }}
                      className="form-control"
                      placeholder="Remaining amount"
                    />
                  </FormGroup>

                  <div className="alert alert-warning mb-0">
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="fw-medium text-warning-emphasis">
                        <i className="ri-money-dollar-circle-line me-1"></i> Total Payable
                      </span>
                      <strong className="fs-5 text-dark">₹{selectedBookingForPayment.finalTotal || selectedBookingForPayment.finalPrice || 0}</strong>
                    </div>
                    <hr className="my-2 border-warning-subtle opacity-50" />
                    <div className="d-flex justify-content-between text-muted small">
                      <span>Paid: <span className="text-dark fw-medium">₹{partialPayment.paidAmount || 0}</span></span>
                      <span>Remaining: <span className="text-dark fw-medium">₹{partialPayment.remainingAmount || 0}</span></span>
                    </div>
                  </div>
                </div>
              )}

              {paymentStatus === 'paid' && (
                <div className="alert alert-success">
                  <i className="ri-check-double-line me-2"></i>
                  Full payment of ₹{selectedBookingForPayment.finalTotal || selectedBookingForPayment.finalPrice || 0} will be marked as paid.
                </div>
              )}

              {paymentStatus === 'incomplete' && (
                <div className="alert alert-warning">
                  <i className="ri-alert-line me-2"></i>
                  Payment will be marked as not paid (₹0 paid, ₹{selectedBookingForPayment.finalTotal || selectedBookingForPayment.finalPrice || 0} remaining).
                </div>
              )}
            </div>
          )}
        </ModalBody>
        <ModalFooter style={{ borderTop: '1px solid #dee2e6' }}>
          <Button color="light" onClick={() => setPaymentModalOpen(false)} disabled={updatingPayment}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleUpdatePaymentStatus} disabled={updatingPayment}>
            {updatingPayment ? (
              <>
                <Spinner size="sm" className="me-2" />
                Updating...
              </>
            ) : (
              <>
                <i className="ri-check-line me-1"></i>
                Update Payment
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Edit Booking Modal */}
      {/* Edit Booking Modal */}
      <Modal isOpen={editModalOpen} toggle={() => setEditModalOpen(false)} size="xl" centered className="full-height-model custom-modal-refined">
        <ModalHeader toggle={() => setEditModalOpen(false)} style={{ borderBottom: '1px solid #dee2e6' }}>
          <i className="ri-edit-line me-2"></i>
          Edit Booking - #{editBookingData?.id || 'N/A'}
        </ModalHeader>

        <ModalBody style={{ flex: 1, overflowY: 'auto', backgroundColor: '#f5f5f5', padding: '0' }}>
          <Container fluid className="pt-3">
            <Form>

              {/* Card 1: Customer & Payment Basics */}
              <Card className="border-0 shadow-sm mb-3">
                <CardBody className="p-4">
                  <h6 className="mb-3 fw-semibold border-bottom pb-2" style={{ color: '#00209F' }}>
                    <i className="ri-user-line me-2"></i>
                    Customer & Payment Overview
                  </h6>

                  <div className="border rounded p-3 mb-4" style={{ backgroundColor: '#f5f5f5' }}>
                    <Row className="align-items-center">
                      <Col md={6}>
                        <div className="d-flex align-items-center">
                          <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px', backgroundColor: 'rgba(0, 32, 159, 0.1)' }}>
                            <i className="ri-user-3-line" style={{ color: '#00209F' }}></i>
                          </div>
                          <div>
                            <div className="text-muted small fw-medium">Customer</div>
                            <div className="fs-5 fw-semibold">{editBookingData.customerName || 'N/A'}</div>
                          </div>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="d-flex align-items-center">
                          <div className="rounded-circle d-flex align-items-center justify-content-center me-3 bg-light" style={{ width: '40px', height: '40px' }}>
                            <i className="ri-phone-line text-primary"></i>
                          </div>
                          <div>
                            <div className="text-muted small fw-medium">Contact</div>
                            <div className="fs-5 fw-semibold">{editBookingData.customerContact || 'N/A'}</div>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </div>

                  <Row className="g-3">
                    <Col md={4}>
                      <FormGroup>
                        <Label className="fw-medium">No. of Persons *</Label>
                        <Input
                          type="number"
                          value={editBookingData.numberOfPersons === "" ? "" : (editBookingData.numberOfPersons === 0 || editBookingData.numberOfPersons == null ? "1" : editBookingData.numberOfPersons)}
                          onChange={(e) => {
                            const val = e.target.value;
                            const persons = val === "" ? "" : (parseInt(val, 10) || 0);
                            const personsNum = val === "" ? 1 : (parseInt(val, 10) || 1);

                            setEditBookingData(prev => {
                              const maxPeople = prev.farmMaxPersons || 10;
                              const extraPrice = prev.extraPersonPrice || 0;
                              let extraCharge = 0;
                              if (personsNum > maxPeople) {
                                extraCharge = (personsNum - maxPeople) * extraPrice;
                              }

                              const original = prev.originalPrice || 0;
                              const discount = prev.discountAmount || 0;
                              const finalPrice = original + extraCharge - discount;
                              const paid = prev.paidAmount || 0;

                              return {
                                ...prev,
                                numberOfPersons: persons,
                                extraPersonCharge: extraCharge,
                                finalPrice: finalPrice,
                                remainingAmount: Math.max(0, finalPrice - paid)
                              };
                            });
                          }}
                          onFocus={(e) => {
                            if (e.target.value === "1" || e.target.value === "0" || Number(e.target.value) === 1) {
                              setEditBookingData(prev => ({ ...prev, numberOfPersons: "" }));
                            }
                          }}
                          onBlur={(e) => {
                            const val = e.target.value;
                            if (val === "" || val === null) {
                              setEditBookingData(prev => ({ ...prev, numberOfPersons: 1 }));
                            }
                          }}
                          min="1"
                          className="form-control"
                        />
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup>
                        <Label className="fw-medium">Payment Status</Label>
                        <Input
                          type="select"
                          value={editBookingData.paymentStatus || 'incomplete'}
                          onChange={(e) => {
                            const newStatus = e.target.value;
                            const total = editBookingData.finalPrice || 0;
                            setEditBookingData(prev => {
                              let paid = prev.paidAmount || 0;
                              if (newStatus === 'paid') paid = total;
                              else if (newStatus === 'incomplete') paid = 0;
                              return { ...prev, paymentStatus: newStatus, paidAmount: paid, remainingAmount: Math.max(0, total - paid) };
                            });
                          }}
                        >
                          <option value="incomplete">Not Paid</option>
                          <option value="partial">Outstanding</option>
                          <option value="paid">Paid</option>
                          <option value="cancel">Cancel</option>
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup>
                        <Label className="fw-medium">Paid Amount (₹)</Label>
                        <Input
                          type="number"
                          value={editBookingData.paidAmount === "" ? "" : (editBookingData.paidAmount === 0 || editBookingData.paidAmount == null ? "0" : editBookingData.paidAmount)}
                          onChange={(e) => {
                            const val = e.target.value;
                            const paidForState = val === "" ? "" : parseFloat(val);
                            const paidForCalc = val === "" ? 0 : (parseFloat(val) || 0);
                            const total = editBookingData.finalPrice || 0;
                            setEditBookingData(prev => ({
                              ...prev,
                              paidAmount: paidForState,
                              remainingAmount: Math.max(0, total - paidForCalc),
                              paymentStatus: paidForCalc >= total ? 'paid' : (paidForCalc > 0 ? 'partial' : 'incomplete')
                            }));
                          }}
                          onFocus={(e) => {
                            if (e.target.value === "0" || Number(e.target.value) === 0) {
                              setEditBookingData(prev => ({ ...prev, paidAmount: "" }));
                            }
                          }}
                          onBlur={(e) => {
                            if (e.target.value === "" || editBookingData.paidAmount === "") {
                              setEditBookingData(prev => ({ ...prev, paidAmount: 0 }));
                            }
                          }}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              {/* Card 2: Booking Timeline */}
              <Card className="border-0 shadow-sm mb-3">
                <CardBody className="p-4">
                  <h6 className="mb-3 fw-semibold border-bottom pb-2" style={{ color: '#000' }}>
                    <i className="ri-calendar-event-line me-2"></i>
                    Booking Timeline & Property
                  </h6>
                  <Row className="g-3">
                    <Col md={6}>
                      <FormGroup>
                        <Label className="fw-medium">Check-in Date *</Label>
                        <Flatpickr
                          className="form-control d-block"
                          options={{ dateFormat: "d M, Y", disableMobile: true }}
                          value={editBookingData.bookingDate ? new Date(editBookingData.bookingDate + 'T00:00:00') : ""}
                          onChange={handleEditCheckInDateChange}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label className="fw-medium">Check-out Date *</Label>
                        <Flatpickr
                          className="form-control d-block"
                          options={{
                            dateFormat: "d M, Y",
                            minDate: editBookingData.bookingDate || undefined,
                            disableMobile: true,
                          }}
                          value={editBookingData.bookingEndDate ? new Date(editBookingData.bookingEndDate + 'T00:00:00') : ""}
                          onChange={handleEditCheckOutDateChange}
                        />
                      </FormGroup>
                    </Col>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <Col md={6}>
                        <FormGroup>
                          <Label className="fw-medium">Check-in Time *</Label>
                          <div className="position-relative">
                            <Input
                              innerRef={editCheckInTimeRef}
                              type="text"
                              className="bg-white pe-5"
                              value={formatTime(editBookingData.bookingTimeFrom)}
                              readOnly
                              onClick={() => {
                                setEditCheckOutPickerOpen(false);
                                setEditCheckInPickerOpen((prev) => !prev);
                              }}
                            />
                            <i
                              className="ri-time-line text-muted position-absolute"
                              style={{ right: "12px", top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}
                            ></i>
                          </div>
                          <DesktopTimePicker
                            open={editCheckInPickerOpen}
                            onOpen={() => setEditCheckOutPickerOpen(false)}
                            onClose={() => setEditCheckInPickerOpen(false)}
                            value={editBookingData.bookingTimeFrom ? dayjs(`2000-01-01T${editBookingData.bookingTimeFrom}`) : null}
                            enableAccessibleFieldDOMStructure={false}
                            closeOnSelect
                            ampm
                            minutesStep={1}
                            onChange={(value) => {
                              if (!value || !value.isValid()) return;
                              const timeString = value.format("HH:mm");

                              const selectedFarm = editAvailableFarms.find((f) => f.id === editBookingData.farmhouseId);
                              let newOriginalPrice = editBookingData.originalPrice;
                              let extraCharge = editBookingData.extraPersonCharge || 0;
                              let extraPrice = editBookingData.extraPersonPrice || 0;
                              let maxPeople = editBookingData.farmMaxPersons || 10;
                              let newBookingType = editBookingData.bookingType || "HOURLY_BOOKING";

                              if (selectedFarm) {
                                const { rent, hours } = calculateDurationAndRent(
                                  editBookingData.bookingDate,
                                  editBookingData.bookingEndDate,
                                  timeString,
                                  editBookingData.bookingTimeTo,
                                  selectedFarm
                                );
                                newOriginalPrice = rent || selectedFarm.price || 0;

                                const typePrefix = isWeekend(editBookingData.bookingDate) ? "WEEKEND" : "REGULAR";
                                const derivedType = hours > 12 ? "24HR" : "12HR";
                                newBookingType = `${typePrefix}_${derivedType}`;
                                const priceOption = selectedFarm.priceOptions?.find((o) => o.category === newBookingType);
                                maxPeople = priceOption?.maxPeople || selectedFarm.maxPersons || 10;
                                extraPrice = priceOption?.extraPersonPrice || 0;

                                const personsNum = Number(editBookingData.numberOfPersons) || 1;
                                extraCharge = personsNum > maxPeople ? (personsNum - maxPeople) * extraPrice : 0;
                              }

                              setEditBookingData((prev) => {
                                const finalPrice = (newOriginalPrice || 0) + extraCharge - (prev.discountAmount || 0);
                                return {
                                  ...prev,
                                  bookingTimeFrom: timeString,
                                  bookingType: newBookingType,
                                  suggestedPrice: newOriginalPrice,
                                  extraPersonCharge: extraCharge,
                                  extraPersonPrice: extraPrice,
                                  farmMaxPersons: maxPeople,
                                  finalPrice,
                                  remainingAmount: Math.max(0, finalPrice - (prev.paidAmount || 0)),
                                };
                              });
                              setEditCheckInPickerOpen(false);
                            }}
                            slotProps={{
                              textField: {
                                sx: { display: "none" },
                              },
                              popper: {
                                anchorEl: editCheckInTimeRef.current,
                                sx: {
                                  "& .MuiPaper-root, & .MuiPickersLayout-root, & .MuiMultiSectionDigitalClock-root, & .MuiMultiSectionDigitalClockSection-root, & .MuiDialogActions-root": {
                                    backgroundColor: "#fff",
                                  },
                                },
                              },
                              actionBar: {
                                actions: ["cancel", "accept"],
                              },
                            }}
                          />
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label className="fw-medium">Check-out Time *</Label>
                          <div className="position-relative">
                            <Input
                              innerRef={editCheckOutTimeRef}
                              type="text"
                              className="bg-white pe-5"
                              value={formatTime(editBookingData.bookingTimeTo)}
                              readOnly
                              onClick={() => {
                                setEditCheckInPickerOpen(false);
                                setEditCheckOutPickerOpen((prev) => !prev);
                              }}
                            />
                            <i
                              className="ri-time-line text-muted position-absolute"
                              style={{ right: "12px", top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}
                            ></i>
                          </div>
                          <DesktopTimePicker
                            open={editCheckOutPickerOpen}
                            onOpen={() => setEditCheckInPickerOpen(false)}
                            onClose={() => setEditCheckOutPickerOpen(false)}
                            value={editBookingData.bookingTimeTo ? dayjs(`2000-01-01T${editBookingData.bookingTimeTo}`) : null}
                            enableAccessibleFieldDOMStructure={false}
                            closeOnSelect
                            ampm
                            minutesStep={1}
                            onChange={(value) => {
                              if (!value || !value.isValid()) return;
                              const timeString = value.format("HH:mm");

                              const selectedFarm = editAvailableFarms.find((f) => f.id === editBookingData.farmhouseId);
                              let newOriginalPrice = editBookingData.originalPrice;
                              let extraCharge = editBookingData.extraPersonCharge || 0;
                              let extraPrice = editBookingData.extraPersonPrice || 0;
                              let maxPeople = editBookingData.farmMaxPersons || 10;
                              let newBookingType = editBookingData.bookingType || "HOURLY_BOOKING";

                              if (selectedFarm) {
                                const { rent, hours } = calculateDurationAndRent(
                                  editBookingData.bookingDate,
                                  editBookingData.bookingEndDate,
                                  editBookingData.bookingTimeFrom,
                                  timeString,
                                  selectedFarm
                                );
                                newOriginalPrice = rent || selectedFarm.price || 0;

                                const typePrefix = isWeekend(editBookingData.bookingDate) ? "WEEKEND" : "REGULAR";
                                const derivedType = hours > 12 ? "24HR" : "12HR";
                                newBookingType = `${typePrefix}_${derivedType}`;
                                const priceOption = selectedFarm.priceOptions?.find((o) => o.category === newBookingType);
                                maxPeople = priceOption?.maxPeople || selectedFarm.maxPersons || 10;
                                extraPrice = priceOption?.extraPersonPrice || 0;

                                const personsNum = Number(editBookingData.numberOfPersons) || 1;
                                extraCharge = personsNum > maxPeople ? (personsNum - maxPeople) * extraPrice : 0;
                              }

                              setEditBookingData((prev) => {
                                const finalPrice = (newOriginalPrice || 0) + extraCharge - (prev.discountAmount || 0);
                                return {
                                  ...prev,
                                  bookingTimeTo: timeString,
                                  bookingType: newBookingType,
                                  suggestedPrice: newOriginalPrice,
                                  extraPersonCharge: extraCharge,
                                  extraPersonPrice: extraPrice,
                                  farmMaxPersons: maxPeople,
                                  finalPrice,
                                  remainingAmount: Math.max(0, finalPrice - (prev.paidAmount || 0)),
                                };
                              });
                              setEditCheckOutPickerOpen(false);
                            }}
                            slotProps={{
                              textField: {
                                sx: { display: "none" },
                              },
                              popper: {
                                anchorEl: editCheckOutTimeRef.current,
                                sx: {
                                  "& .MuiPaper-root, & .MuiPickersLayout-root, & .MuiMultiSectionDigitalClock-root, & .MuiMultiSectionDigitalClockSection-root, & .MuiDialogActions-root": {
                                    backgroundColor: "#fff",
                                  },
                                },
                              },
                              actionBar: {
                                actions: ["cancel", "accept"],
                              },
                            }}
                          />
                        </FormGroup>
                      </Col>
                    </LocalizationProvider>

                    <Col md={6}>
                      <FormGroup>
                        <Label className="fw-medium">Farmhouse</Label>
                        <Input
                          type="select"
                          value={editBookingData.farmhouseId || ''}
                          onChange={(e) => {
                            setEditCheckInPickerOpen(false);
                            setEditCheckOutPickerOpen(false);
                            const farmId = e.target.value;
                            const selectedFarm = editAvailableFarms.find(f => f.id === farmId);
                            let newOriginalPrice = editBookingData.originalPrice;
                            let extraCharge = editBookingData.extraPersonCharge || 0;
                            let extraPrice = editBookingData.extraPersonPrice || 0;
                            let maxPeople = editBookingData.farmMaxPersons || 10;
                            let newBookingType = editBookingData.bookingType || "HOURLY_BOOKING";

                            if (selectedFarm) {
                              const { rent, hours } = calculateDurationAndRent(
                                editBookingData.bookingDate,
                                editBookingData.bookingEndDate,
                                editBookingData.bookingTimeFrom,
                                editBookingData.bookingTimeTo,
                                selectedFarm
                              );
                              newOriginalPrice = rent || selectedFarm.price || 0;

                              const typePrefix = isWeekend(editBookingData.bookingDate) ? "WEEKEND" : "REGULAR";
                              const derivedType = (hours > 12 ? "24HR" : "12HR");
                              newBookingType = `${typePrefix}_${derivedType}`;
                              const priceOption = selectedFarm.priceOptions?.find((o) => o.category === newBookingType);
                              maxPeople = priceOption?.maxPeople || selectedFarm.maxPersons || 10;
                              extraPrice = priceOption?.extraPersonPrice || 0;

                              const personsNum = Number(editBookingData.numberOfPersons) || 1;
                              if (personsNum > maxPeople) {
                                extraCharge = (personsNum - maxPeople) * extraPrice;
                              } else {
                                extraCharge = 0;
                              }
                            }

                            setEditBookingData(prev => {
                              const finalPrice = (prev.originalPrice || 0) + extraCharge - (prev.discountAmount || 0);
                              return {
                                ...prev,
                                farmhouseId: farmId,
                                bookingType: newBookingType,
                                suggestedPrice: newOriginalPrice,
                                extraPersonCharge: extraCharge,
                                extraPersonPrice: extraPrice,
                                farmMaxPersons: maxPeople,
                                finalPrice: finalPrice,
                                remainingAmount: Math.max(0, finalPrice - (prev.paidAmount || 0))
                              };
                            });
                          }}
                        >
                          <option value="">Select Farmhouse</option>
                          {editAvailableFarms.map(farm => (
                            <option key={farm.id} value={farm.id}>{farm.name} (#{farm.farmNo})</option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                  </Row>

                  {editBookingData.bookingDate &&
                    editBookingData.bookingEndDate &&
                    editBookingData.bookingTimeFrom &&
                    editBookingData.bookingTimeTo && (
                      <div className="text-muted small mt-2 mb-3">
                        <i className="ri-time-line me-1"></i>
                        Duration: {formatBookingDate(editBookingData.bookingDate)} at {formatTime(editBookingData.bookingTimeFrom)} → {formatBookingDate(editBookingData.bookingEndDate)} at {formatTime(editBookingData.bookingTimeTo)}
                        {(() => {
                          const start = new Date(`${editBookingData.bookingDate}T${editBookingData.bookingTimeFrom}`);
                          const end = new Date(`${editBookingData.bookingEndDate}T${editBookingData.bookingTimeTo}`);
                          const actualHours = Math.round((end - start) / (1000 * 60 * 60));
                          if (actualHours > 0) {
                            const roundedHours = Math.ceil(actualHours / 12) * 12;
                            return ` • Total: ${roundedHours} hours`;
                          }
                          return '';
                        })()}
                      </div>
                    )}
                </CardBody>
              </Card>

              {/* Card 3: Pricing & Payments */}
              <Card className="border-0 shadow-sm mb-3">
                <CardBody className="p-4">
                  <h6 className="mb-3 fw-semibold border-bottom pb-2" style={{ color: '#00209F' }}>
                    <i className="ri-money-dollar-circle-line me-2"></i>
                    Pricing & Payments
                  </h6>

                  <Row className="g-3">
                    <Col md={6}>
                      <FormGroup>
                        <Label className="fw-medium">Base Price (₹)</Label>
                        <Input
                          type="number"
                          value={editBookingData.originalPrice === "" ? "" : (editBookingData.originalPrice || 0)}
                          onChange={(e) => {
                            const val = e.target.value;
                            const originalNum = val === "" ? 0 : parseFloat(val);
                            const originalVal = val === "" ? "" : parseFloat(val);
                            setEditBookingData(prev => {
                              const finalPrice = originalNum + (prev.extraPersonCharge || 0) - (prev.discountAmount || 0);
                              return {
                                ...prev,
                                originalPrice: originalVal,
                                finalPrice: finalPrice,
                                remainingAmount: Math.max(0, finalPrice - (prev.paidAmount || 0))
                              };
                            });
                          }}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label className="fw-medium">Discount Amount (₹)</Label>
                        <Input
                          type="number"
                          value={editBookingData.discountAmount === "" ? "" : (editBookingData.discountAmount || 0)}
                          onChange={(e) => {
                            const val = e.target.value;
                            const discountNum = val === "" ? 0 : parseFloat(val);
                            const discountVal = val === "" ? "" : parseFloat(val);
                            setEditBookingData(prev => {
                              const finalPrice = (prev.originalPrice || 0) + (prev.extraPersonCharge || 0) - discountNum;
                              return {
                                ...prev,
                                discountAmount: discountVal,
                                finalPrice: finalPrice,
                                remainingAmount: Math.max(0, finalPrice - (prev.paidAmount || 0))
                              };
                            });
                          }}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <div className="bg-light p-3 rounded mt-3">
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Current Base Price:</span>
                      <span className="fw-semibold">₹{editBookingData.originalPrice || 0}</span>
                    </div>
                    {editBookingData.suggestedPrice !== editBookingData.originalPrice && (
                      <div className="d-flex justify-content-between mb-2 align-items-center">
                        <span className="text-info small"><i className="ri-information-line me-1"></i> System suggests:</span>
                        <div>
                          <span className="fw-medium text-info me-2">₹{editBookingData.suggestedPrice}</span>
                          <Button
                            size="sm"
                            color="info"
                            outline
                            className="py-0 px-2"
                            style={{ fontSize: '11px' }}
                            onClick={() => {
                              const newBase = editBookingData.suggestedPrice;
                              setEditBookingData(prev => {
                                const final = newBase + (prev.extraPersonCharge || 0) - (prev.discountAmount || 0);
                                return {
                                  ...prev,
                                  originalPrice: newBase,
                                  finalPrice: final,
                                  remainingAmount: Math.max(0, final - (prev.paidAmount || 0))
                                };
                              });
                            }}
                          >
                            Apply
                          </Button>
                        </div>
                      </div>
                    )}
                    {editBookingData.extraPersonCharge > 0 && (
                      <div className="d-flex justify-content-between mb-2 text-danger">
                        <span className="text-muted">Extra Person Charges:</span>
                        <span className="fw-semibold">+₹{editBookingData.extraPersonCharge}</span>
                      </div>
                    )}
                    {editBookingData.discountAmount > 0 && (
                      <div className="d-flex justify-content-between mb-2 text-success">
                        <span className="text-muted">Discount Applied:</span>
                        <span className="fw-semibold">-₹{editBookingData.discountAmount}</span>
                      </div>
                    )}
                    <hr />
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="fw-bold">Final Total Amount:</span>
                      <span className="fs-4 fw-bold text-primary">₹{editBookingData.finalPrice || 0}</span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mt-2">
                      <span className="text-muted small">Paid: ₹{editBookingData.paidAmount || 0}</span>
                      <span className="text-danger fw-medium small">Remaining: ₹{editBookingData.remainingAmount || 0}</span>
                    </div>
                  </div>
                </CardBody>
              </Card>


            </Form>
          </Container>
        </ModalBody>

        <ModalFooter style={{ borderTop: '1px solid #dee2e6', padding: '1rem' }}>
          <div className="d-flex justify-content-end w-100 align-items-center">
            <div className="d-flex gap-2">
              <Button color="light" onClick={() => setEditModalOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateBooking} disabled={isUpdating} style={{ backgroundColor: '#000', borderColor: '#000' }}>
                {isUpdating ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Updating...
                  </>
                ) : (
                  <>
                    <i className="ri-save-line me-1"></i>
                    Update Booking
                  </>
                )}
              </Button>
            </div>
          </div>
        </ModalFooter>
      </Modal>

      {/* Booking Details Modal */}
      <Modal isOpen={detailsModalOpen} toggle={() => setDetailsModalOpen(false)} size="xl" centered className="full-height-model custom-modal-refined">
        <ModalHeader toggle={() => setDetailsModalOpen(false)} style={{ borderBottom: '1px solid #dee2e6' }}>
          <i className="ri-information-line me-2"></i>
          Booking Details
        </ModalHeader>

        <ModalBody style={{ flex: 1, overflowY: 'auto', backgroundColor: '#f5f5f5', padding: '0' }}>
          {bookingDetails ? (
            <Container fluid className="py-3">
              <Row className="g-3">
                <Col lg={6}>
                  <Card className="border-0 shadow-sm h-100">
                    <CardBody className="p-3">
                      <div className="d-flex align-items-center mb-4">
                        <div className="avatar-md flex-shrink-0 bg-primary-subtle rounded-circle d-flex align-items-center justify-content-center me-3">
                          <i className="ri-user-3-line fs-20 text-primary"></i>
                        </div>
                        <div>
                          <h5 className="mb-1 fw-semibold">{bookingDetails.user?.name || "N/A"}</h5>
                          <p className="text-muted mb-0">
                            <i className="ri-phone-line me-1"></i>
                            {bookingDetails.user?.mobileNo || "N/A"}
                          </p>
                        </div>
                      </div>

                      <div className="border-top pt-3">
                        <div className="d-flex justify-content-between align-items-center py-2">
                          <span className="text-muted">Email:</span>
                          <span className="fw-medium">{bookingDetails.user?.email || "N/A"}</span>
                        </div>
                        <div className="d-flex justify-content-between align-items-center py-2 border-top">
                          <span className="text-muted">Customer ID:</span>
                          <span className="fw-medium">{bookingDetails.user?.id || "N/A"}</span>
                        </div>
                        <div className="d-flex justify-content-between align-items-center py-2 border-top">
                          <span className="text-muted">Persons:</span>
                          <Badge color="info" className="fw-bold">
                            {bookingDetails.numberOfPersons || 1}
                          </Badge>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>

                {/* Farmhouse Card */}
                <Col lg={6}>
                  <Card className="border-0 shadow-sm h-100">
                    <CardBody className="p-3">
                      <div className="d-flex align-items-center mb-4">
                        <div className="avatar-md flex-shrink-0 bg-success-subtle rounded-circle d-flex align-items-center justify-content-center me-3">
                          <i className="ri-home-4-line fs-20 text-success"></i>
                        </div>
                        <div>
                          <h5 className="mb-1 fw-semibold">{bookingDetails.farmhouse?.name || "N/A"}</h5>
                          <p className="text-muted mb-0">
                            <i className="ri-map-pin-line me-1"></i>
                            {bookingDetails.farmhouse?.location?.city || "Location not specified"}
                          </p>
                        </div>
                      </div>

                      <div className="border-top pt-3">
                        <div className="d-flex justify-content-between align-items-center py-2">
                          <span className="text-muted">Farm No:</span>
                          <Badge color="success" className="fw-bold">
                            #{bookingDetails.farmhouse?.farmNo || "N/A"}
                          </Badge>
                        </div>
                        <div className="d-flex justify-content-between align-items-center py-2 border-top">
                          <span className="text-muted">Max Capacity:</span>
                          <span className="fw-medium">{bookingDetails.farmhouse?.maxPersons || 'N/A'} persons</span>
                        </div>
                        <div className="d-flex justify-content-between align-items-center py-2 border-top">
                          <span className="text-muted">Extra Person Price:</span>
                          <span className="fw-medium text-primary">
                            ₹{bookingDetails.extraPersonInfo?.extraPersonPricePerPerson ?? bookingDetails.farmhouse?.priceOptions?.find(o => o.category === bookingDetails.bookingType)?.extraPersonPrice ?? 0}/person
                          </span>
                        </div>
                        <div className="d-flex justify-content-between align-items-center py-2 border-top">
                          <span className="text-muted">Booking Capacity:</span>
                          <span className="fw-medium"> {bookingDetails.numberOfPersons || 'N/A'} persons</span>
                        </div>
                        <div className="d-flex justify-content-between align-items-center py-2 border-top">
                          <span className="text-muted">Check-in Time:</span>
                          <span className="fw-medium">{formatTime(bookingDetails.checkInTime || bookingDetails.bookingTimeFrom)}</span>
                        </div>
                        <div className="d-flex justify-content-between align-items-center py-2 border-top">
                          <span className="text-muted">Check-out Time:</span>
                          <span className="fw-medium">{formatTime(bookingDetails.checkOutTime || bookingDetails.bookingTimeTo)}</span>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>

                {/* Timeline Card - Full Width */}
                <Col xs={12}>
                  <Card className="border-0 shadow-sm overflow-hidden">
                    <CardBody className="p-4">
                      <div className="d-flex justify-content-between align-items-center mb-4">
                        <h6 className="mb-0 fw-semibold">
                          <i className="ri-time-line me-2 text-primary fs-18"></i>
                          Booking Timeline
                        </h6>
                        <Badge color="soft-primary" className="text-primary border">
                          {getBookingTypeLabel(bookingDetails.bookingType)}
                        </Badge>
                      </div>

                      <div className="position-relative p-4 bg-light rounded-3 border overflow-hidden">
                        {/* Decorative background element */}
                        <div className="position-absolute top-0 end-0 p-3 opacity-25">
                          <i className="ri-calendar-todo-line text-primary" style={{ fontSize: '120px', transform: 'rotate(15deg)', marginRight: '-30px', marginTop: '-10px' }}></i>
                        </div>

                        <Row className="position-relative" style={{ zIndex: 1 }}>
                          <Col md={5} className="mb-4 mb-md-0">
                            <div className="d-flex align-items-start">
                              <div className="flex-shrink-0">
                                <div className="avatar-sm bg-success-subtle rounded-circle d-flex align-items-center justify-content-center text-success">
                                  <i className="ri-login-box-line fs-20"></i>
                                </div>
                              </div>
                              <div className="flex-grow-1 ms-3">
                                <div className="text-muted small fw-medium text-uppercase mb-1" style={{ letterSpacing: '0.5px' }}>Check-in</div>
                                <div className="fw-bold fs-18 text-dark">{formatBookingDate(bookingDetails.startDate || bookingDetails.bookingDate)}</div>
                                <div className="text-success fw-bold fs-20 mt-1">
                                  {formatTime(bookingDetails.checkInTime || bookingDetails.bookingTimeFrom)}
                                </div>
                              </div>
                            </div>
                          </Col>

                          <Col md={2} className="d-flex align-items-center justify-content-center mb-4 mb-md-0 d-none d-md-flex">
                            <div className="text-center opacity-50">
                              <i className="ri-arrow-right-line fs-24"></i>
                              <div className="small fw-semibold mt-1">
                                {(() => {
                                  try {
                                    const start = new Date(`${bookingDetails.startDate || bookingDetails.bookingDate}T${bookingDetails.checkInTime || bookingDetails.bookingTimeFrom || '07:00'}`);
                                    const end = new Date(`${bookingDetails.endDate || bookingDetails.bookingEndDate || bookingDetails.startDate}T${bookingDetails.checkOutTime || bookingDetails.bookingTimeTo || '18:00'}`);
                                    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
                                      return bookingDetails.bookingHours || 12;
                                    }
                                    const actualHours = Math.round((end - start) / (1000 * 60 * 60));
                                    return Math.ceil(Math.max(0, actualHours) / 12) * 12;
                                  } catch (e) {
                                    return bookingDetails.bookingHours || 12;
                                  }
                                })()}H
                              </div>
                            </div>
                          </Col>

                          <Col md={5}>
                            <div className="d-flex align-items-start">
                              <div className="flex-shrink-0">
                                <div className="avatar-sm bg-danger-subtle rounded-circle d-flex align-items-center justify-content-center text-danger">
                                  <i className="ri-logout-box-line fs-20"></i>
                                </div>
                              </div>
                              <div className="flex-grow-1 ms-3">
                                <div className="text-muted small fw-medium text-uppercase mb-1" style={{ letterSpacing: '0.5px' }}>Check-out</div>
                                <div className="fw-bold fs-18 text-dark">{formatBookingDate(bookingDetails.endDate || bookingDetails.bookingEndDate || bookingDetails.startDate)}</div>
                                <div className="text-danger fw-bold fs-20 mt-1">
                                  {formatTime(bookingDetails.checkOutTime || bookingDetails.bookingTimeTo)}
                                </div>
                              </div>
                            </div>
                          </Col>
                        </Row>

                        {/* Visual Progress Bar */}
                        <div className="progress mt-4 border" style={{ height: '8px', backgroundColor: '#e2e8f0' }}>
                          <div
                            className="progress-bar bg-primary progress-bar-striped progress-bar-animated"
                            role="progressbar"
                            style={{ width: '100%' }}
                            aria-valuenow="100"
                            aria-valuemin="0"
                            aria-valuemax="100"
                          ></div>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>

                {/* Payment Details Card - Full Width */}
                <Col xs={12}>
                  <Card className="border-0 shadow-sm">
                    <CardBody className="p-4">
                      <h6 className="mb-4 fw-semibold border-bottom pb-2">
                        <i className="ri-money-dollar-circle-line me-2 text-success"></i>Payment Information
                      </h6>
                      <Row>
                        <Col md={3} className="mb-3">
                          <div className="text-center p-3 border rounded h-100">
                            <div className="text-muted fs-12">Original Price</div>
                            <div className="fw-bold fs-18 mt-2">₹{bookingDetails.originalPrice || 0}</div>
                            <small className="text-muted d-block mt-1">Base amount</small>
                          </div>
                        </Col>
                        <Col md={3} className="mb-3">
                          <div className="text-center p-3 border rounded h-100">
                            <div className="text-muted fs-12">Discount Applied</div>
                            <div className="fw-bold fs-18 text-danger mt-2">-₹{bookingDetails.discountAmount || 0}</div>
                            <small className="text-muted d-block mt-1">Special discount</small>
                          </div>
                        </Col>
                        <Col md={3} className="mb-3">
                          <div className="text-center p-3 border rounded bg-success-subtle h-100">
                            <div className="text-muted fs-12">Final Amount</div>
                            <div className="fw-bold fs-18 text-success mt-2">
                              ₹{bookingDetails.finalTotal || bookingDetails.finalPrice || 0}
                            </div>
                            <small className="text-success d-block mt-1 fw-medium">Total payable</small>
                          </div>
                        </Col>
                        <Col md={3} className="mb-3">
                          <div className="text-center p-3 border rounded h-100 d-flex flex-column justify-content-center">
                            <div className="text-muted fs-12 mb-2">Payment Status</div>
                            <div className="mb-3">
                              <Badge
                                color={getPaymentStatusColor(bookingDetails.paymentStatus)}
                                className="fw-bold fs-14 px-3 py-2"
                              >
                                {(bookingDetails.paymentStatus || 'incomplete').toUpperCase()}
                              </Badge>
                            </div>
                          </div>
                        </Col>
                      </Row>

                      {/* Extra Person Information */}
                      {bookingDetails.extraPersonInfo && bookingDetails.extraPersonInfo.extraPersonCount > 0 && (
                        <Row className="mt-4">
                          <Col xs={12}>
                            <Card className="border border-info-subtle bg-info-subtle">
                              <CardBody className="p-3">
                                <h6 className="mb-3">
                                  <i className="ri-user-add-line me-2"></i>
                                  Extra Person Charges
                                </h6>
                                <Row>
                                  <Col md={3} className="mb-2">
                                    <div className="d-flex justify-content-between align-items-center">
                                      <span className="text-muted">Max Persons:</span>
                                      <span className="fw-bold">{bookingDetails.extraPersonInfo.maxPersons || bookingDetails.farmhouse?.maxPersons || 0}</span>
                                    </div>
                                  </Col>
                                  <Col md={3} className="mb-2">
                                    <div className="d-flex justify-content-between align-items-center">
                                      <span className="text-muted">Total Persons:</span>
                                      <span className="fw-bold">{bookingDetails.extraPersonInfo.numberOfPersons || bookingDetails.numberOfPersons || 0}</span>
                                    </div>
                                  </Col>
                                  <Col md={3} className="mb-2">
                                    <div className="d-flex justify-content-between align-items-center">
                                      <span className="text-muted">Extra Persons:</span>
                                      <span className="fw-bold text-warning">{bookingDetails.extraPersonInfo.extraPersonCount || 0}</span>
                                    </div>
                                  </Col>
                                  <Col md={3} className="mb-2">
                                    <div className="d-flex justify-content-between align-items-center">
                                      <span className="text-muted">Price per Person:</span>
                                      <span className="fw-bold">₹{bookingDetails.extraPersonInfo.extraPersonPricePerPerson ?? bookingDetails.farmhouse?.priceOptions?.find(o => o.category === bookingDetails.bookingType)?.extraPersonPrice ?? 0}</span>
                                    </div>
                                  </Col>
                                  <Col xs={12} className="mt-2 pt-2 border-top">
                                    <div className="d-flex justify-content-between align-items-center">
                                      <span className="text-muted fw-medium">Extra Person Total:</span>
                                      <span className="fw-bold text-primary fs-5">₹{bookingDetails.extraPersonInfo.extraPersonTotalPrice || 0}</span>
                                    </div>
                                    <small className="text-muted d-block mt-1">
                                      Calculation: {bookingDetails.extraPersonInfo.extraPersonCount || 0} extra persons × ₹{bookingDetails.extraPersonInfo.extraPersonPricePerPerson || 0} = ₹{bookingDetails.extraPersonInfo.extraPersonTotalPrice || 0}
                                    </small>
                                  </Col>
                                </Row>
                              </CardBody>
                            </Card>
                          </Col>
                        </Row>
                      )}

                      {/* Partial Payment Details */}
                      {bookingDetails.paymentStatus === 'partial' && (
                        <Row className="mt-4">
                          <Col xs={12}>
                            <Card className="border border-warning-subtle bg-warning-subtle">
                              <CardBody className="p-3">
                                <h6 className="mb-3">
                                  <i className="ri-money-dollar-circle-line me-2"></i>
                                  Outstanding Payment Details
                                </h6>
                                <Row>
                                  <Col md={4} className="mb-2">
                                    <div className="d-flex justify-content-between align-items-center">
                                      <span className="text-muted">Paid Amount:</span>
                                      <span className="fw-bold text-success fs-5">₹{bookingDetails.paidAmount || 0}</span>
                                    </div>
                                  </Col>
                                  <Col md={4} className="mb-2">
                                    <div className="d-flex justify-content-between align-items-center">
                                      <span className="text-muted">Remaining Amount:</span>
                                      <span className="fw-bold text-danger fs-5">₹{bookingDetails.remainingAmount || 0}</span>
                                    </div>
                                  </Col>
                                  <Col md={4} className="mb-2">
                                    <div className="d-flex justify-content-between align-items-center">
                                      <span className="text-muted">Total Amount:</span>
                                      <span className="fw-bold fs-5">₹{bookingDetails.finalTotal || bookingDetails.finalPrice || 0}</span>
                                    </div>
                                  </Col>
                                </Row>
                                <div className="progress mt-3" style={{ height: '10px' }}>
                                  <div
                                    className="progress-bar bg-success"
                                    role="progressbar"
                                    style={{
                                      width: `${((bookingDetails.paidAmount || 0) / (bookingDetails.finalTotal || bookingDetails.finalPrice || 1)) * 100}%`
                                    }}
                                    aria-valuenow={((bookingDetails.paidAmount || 0) / (bookingDetails.finalTotal || bookingDetails.finalPrice || 1)) * 100}
                                    aria-valuemin="0"
                                    aria-valuemax="100"
                                  >
                                  </div>
                                </div>
                                <div className="d-flex justify-content-between mt-1">
                                  <small className="text-success">₹{bookingDetails.paidAmount || 0} paid</small>
                                  <small className="text-danger">₹{bookingDetails.remainingAmount || 0} remaining</small>
                                </div>
                              </CardBody>
                            </Card>
                          </Col>
                        </Row>
                      )}

                      {bookingDetails.paymentStatus === 'paid' && (
                        <Row className="mt-4">
                          <Col xs={12}>
                            <div className="alert alert-success">
                              <i className="ri-check-double-line me-2"></i>
                              Full payment of ₹{bookingDetails.finalTotal || bookingDetails.finalPrice || 0} has been received.
                            </div>
                          </Col>
                        </Row>
                      )}

                      {bookingDetails.paymentStatus === 'incomplete' && (
                        <Row className="mt-4">
                          <Col xs={12}>
                            <div className="alert alert-warning">
                              <i className="ri-alert-line me-2"></i>
                              Payment pending: ₹{bookingDetails.finalTotal || bookingDetails.finalPrice || 0} remaining to be paid.
                            </div>
                          </Col>
                        </Row>
                      )}
                    </CardBody>
                  </Card>
                  <div className="mt-3 text-end">
                    <small className="text-muted d-block">
                      <i className="ri-time-line me-1"></i>
                      Last updated: {bookingDetails.updatedAt ? new Date(bookingDetails.updatedAt).toLocaleDateString() : 'N/A'}
                    </small>
                  </div>
                </Col>
              </Row>
            </Container>
          ) : (
            <div className="text-center py-5">
              <Spinner color="primary" />
              <p className="mt-2">Loading booking details...</p>
            </div>
          )}
        </ModalBody>

        <ModalFooter style={{ borderTop: '1px solid #dee2e6', padding: '1rem' }}>
          <div className="d-flex justify-content-between w-100 align-items-center">
            <Button color="light" onClick={() => setDetailsModalOpen(false)}>
              Close
            </Button>
            <div className="d-flex">

              <Button
                color="primary"
                onClick={() => bookingDetails && handleInvoiceDownload(bookingDetails)}
                className="d-flex align-items-center"
              >
                <i className="ri-download-line align-middle me-1"></i> Download Invoice
              </Button>
            </div>
          </div>
        </ModalFooter>
      </Modal >

      <style>{modalStyles}</style>
    </React.Fragment >
  );
};

export default Bookings;  
